import {cn} from '@tempmee/core'
import {type ElementType, type TdHTMLAttributes, forwardRef} from 'react'
import {TableCellVariantsFactory} from './Variants'
import {type TableSize, useTableContext, useTableSection} from './context'

export type TableCellVariant = 'head' | 'body' | 'footer'
export type TableCellAlign = 'inherit' | 'left' | 'center' | 'right' | 'justify'
export type TableCellPadding = 'normal' | 'checkbox' | 'none'

export interface TableCellProps extends Omit<TdHTMLAttributes<HTMLTableCellElement>, 'align'> {
  /**
   * Cell role. Defaults from the parent section (`TableHead` → `head`,
   * `TableFooter` → `footer`, otherwise `body`). `head` renders a bold
   * `<th scope="col">`; the rest render `<td>`.
   */
  variant?: TableCellVariant
  /** Text alignment. @default 'inherit' */
  align?: TableCellAlign
  /** Spacing preset: `normal` (16px), `checkbox` (tight leading gutter), `none`. @default 'normal' */
  padding?: TableCellPadding
  /** Override the row density inherited from `Table`. */
  size?: TableSize
  /** Override the rendered element (e.g. force `'td'` inside a head row). */
  component?: ElementType
}

/**
 * TableCell (v3, MUI-aligned) — a single `<th>`/`<td>`. Mirrors
 * `@mui/material/TableCell`: `variant` (`head`/`body`/`footer`), `align`,
 * `padding` (`normal`/`checkbox`/`none`), and `size`. It reads its section and
 * density from context, so inside a `TableHead` it renders a bold header cell
 * with `scope="col"` automatically. Compose any content (text, `Avatar`,
 * `Chip`, `Label`, `IconButton`, `InputSelectField`) as children.
 */
export const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(
  (
    {className, variant, align = 'inherit', padding = 'normal', size, component, scope, ...props},
    ref,
  ) => {
    const section = useTableSection()
    const {size: contextSize} = useTableContext()

    const resolvedVariant: TableCellVariant = variant ?? section ?? 'body'
    const resolvedSize = size ?? contextSize
    const isHeader = resolvedVariant === 'head'
    const Component: ElementType = component ?? (isHeader ? 'th' : 'td')

    return (
      <Component
        ref={ref}
        scope={isHeader && Component === 'th' ? (scope ?? 'col') : scope}
        className={cn(
          TableCellVariantsFactory({variant: resolvedVariant, align, padding, size: resolvedSize}),
          className,
        )}
        {...props}
      />
    )
  },
)
TableCell.displayName = 'TableCell'
